import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import { useSearchParams } from "react-router-dom";
import { safeJSONParse } from "../../../utils/helper";

type QuestionGenerationAgentProps = {
  parsedContent: any;
};

type Question = {
  question: string;
  competency: string;
  rationale: string;
  cascade_questions: {
    strong_answer: string;
    vague_answer: string;
    missing_evidence: string;
  };
};

type QuestionsByCompetency = {
  technical?: Question[];
  behavioral?: Question[];
  communication?: Question[];
  future_ready?: Question[];
};

type CompetencyKey = keyof QuestionsByCompetency;

type InterviewPackData = {
  interview_pack_id: string;
  candidate_information: {
    candidate_id: string;
    email: string | null;
    experience: string | null;
    resume_link: string | null;
  };
  role_information: {
    role: string;
    job_family: string;
    job_description: string;
    job_description_type: string;
    language: string;
  };
  recommended_duration: string;
  questions_by_competency: QuestionsByCompetency;
  do_not_repeat_list: string[];
  must_have_validations: string[];
  pack_summary: {
    total_questions: number;
    technical_count: number;
    behavioral_count: number;
    communication_count: number;
    future_ready_count: number;
    pre_screening_topics_excluded: number;
    must_have_validations_added: number;
    cascade_logic_added: boolean;
    compliance_confirmed: boolean;
  };
  download_url: string;
  pdf_download_url: string;
  pdf_filename: string;
};

const COMPETENCY_LABELS: Record<string, string> = {
  technical: "Technical",
  behavioral: "Behavioural Competency",
  communication: "Communication Skills",
  future_ready: "Future-Ready Skills",
};

const COMPETENCY_COLORS: Record<string, string> = {
  technical: "#00d2aa",
  behavioral: "#9c27b0",
  communication: "#ff9800",
  future_ready: "#2196f3",
};

export default function QuestionGenerationAgent({
  parsedContent,
}: QuestionGenerationAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [cascadeDialogOpen, setCascadeDialogOpen] = useState(false);
  const [selectedCascade, setSelectedCascade] = useState<
    Question["cascade_questions"] | null
  >(null);
  const [hasRegenerated, setHasRegenerated] = useState(false);
  const [selectedCompetency, setSelectedCompetency] =
    useState<CompetencyKey>("technical");

  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );

  const sessionId = useMemo(
    () => searchParams.get("sessionId"),
    [searchParams],
  );

  // Parse the interview pack data
  const interviewPackData: InterviewPackData | null = useMemo(() => {
    try {
      const finalAnswer = parsedContent?.finalAnswer;
      if (Array.isArray(finalAnswer) && finalAnswer.length > 0) {
        return safeJSONParse(finalAnswer[0]);
      }
      return null;
    } catch (error) {
      console.error("Error parsing interview pack data:", error);
      return null;
    }
  }, [parsedContent]);

  const handleDownloadPDF = useCallback(() => {
    if (interviewPackData?.pdf_download_url) {
      window.open(interviewPackData.pdf_download_url, "_blank");
    }
  }, [interviewPackData]);

  const handleViewCascade = useCallback(
    (cascade: Question["cascade_questions"]) => {
      setSelectedCascade(cascade);
      setCascadeDialogOpen(true);
    },
    [],
  );

  const handleCloseCascade = useCallback(() => {
    setCascadeDialogOpen(false);
    setSelectedCascade(null);
  }, []);

  const handleRegeneratePack = useCallback(async () => {
    if (!currentAgent || hasRegenerated) return;

    setHasRegenerated(true);

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload = {
      agentId: currentAgent.id,
      message: "regenerate interview pack",
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
    };

    try {
      await dispatch(sendMessageToAgent(payload)).unwrap();
    } catch (error) {
      console.error("Error regenerating interview pack:", error);
    }
  }, [currentAgent, sessionId, dispatch, hasRegenerated]);

  const handleCompetencyClick = useCallback((competency: CompetencyKey) => {
    setSelectedCompetency(competency);
  }, []);

  if (!interviewPackData) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ color: "#666", fontSize: "14px" }}>
          Unable to load interview pack data.
        </Typography>
      </Box>
    );
  }

  const {
    interview_pack_id,
    candidate_information,
    role_information,
    recommended_duration,
    questions_by_competency,
    do_not_repeat_list,
    must_have_validations,
    pack_summary,
  } = interviewPackData;

  // Extract candidate name from candidate_id
  const candidateName = candidate_information?.candidate_id
    ? candidate_information.candidate_id
    : "Candidate";

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Typography
        sx={{ fontWeight: 600, color: "#004141", mb: 3, fontSize: "16px" }}
      >
        INTERVIEW QUESTION DESIGN
      </Typography>

      {/* Interview Pack Info */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 3,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#888",
              mb: 0.5,
              textTransform: "uppercase",
            }}
          >
            Interview Pack ID
          </Typography>
          <Typography
            sx={{ fontSize: "14px", color: "#004141", fontWeight: 600 }}
          >
            {interview_pack_id}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#888",
              mb: 0.5,
              textTransform: "uppercase",
            }}
          >
            Candidate ID
          </Typography>
          <Typography
            sx={{ fontSize: "14px", color: "#004141", fontWeight: 600 }}
          >
            {candidateName}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#888",
              mb: 0.5,
              textTransform: "uppercase",
            }}
          >
            Role
          </Typography>
          <Typography
            sx={{ fontSize: "14px", color: "#004141", fontWeight: 600 }}
          >
            {role_information.role}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              color: "#888",
              mb: 0.5,
              textTransform: "uppercase",
            }}
          >
            Recommended Duration
          </Typography>
          <Typography
            sx={{ fontSize: "14px", color: "#004141", fontWeight: 600 }}
          >
            {recommended_duration}
          </Typography>
        </Box>
      </Box>

      {/* Action Buttons - Top Right */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 3 }}>
        <Button
          variant="outlined"
          onClick={handleRegeneratePack}
          disabled={hasRegenerated}
          sx={{
            fontSize: "14px",
            textTransform: "none",
            borderColor: "#e0e0e0",
            color: "#666",
            padding: "8px 20px",
            borderRadius: "6px",
            "&:hover": {
              borderColor: "#999",
              backgroundColor: "#f8f9fa",
            },
            "&:disabled": {
              borderColor: "#e0e0e0",
              color: "#ccc",
            },
          }}
        >
          Regenerate Interview Pack
        </Button>
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            backgroundColor: "#00d2aa",
            color: "#fff",
            fontWeight: 500,
            fontSize: "14px",
            textTransform: "none",
            padding: "8px 20px",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "#00b894",
            },
          }}
        >
          Download PDF
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{
          borderBottom: "1px solid #e0e0e0",
          mb: 3,
          "& .MuiTab-root": {
            fontSize: "14px",
            textTransform: "none",
            color: "#888",
            fontWeight: 400,
            minHeight: 48,
            "&.Mui-selected": {
              color: "#00d2aa",
              fontWeight: 600,
            },
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "#00d2aa",
            height: 3,
          },
        }}
      >
        <Tab label="Questions by Competency" />
        <Tab label="Do-Not-Repeat List" />
        <Tab label="Must-Have Validation" />
        <Tab label="Pack Summary" />
      </Tabs>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box sx={{ display: "flex", gap: 3 }}>
          {/* Left Sidebar - Competency List */}
          <Box sx={{ width: 280, flexShrink: 0 }}>
            {(
              Object.entries(questions_by_competency) as [
                CompetencyKey,
                Question[],
              ][]
            ).map(([key, questions]) => (
              <Paper
                key={key}
                elevation={0}
                onClick={() => handleCompetencyClick(key)}
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor:
                    selectedCompetency === key ? "#e6f9f5" : "#f8f9fa",
                  border: `1px solid ${selectedCompetency === key ? "#00d2aa" : "#e0e0e0"}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#e6f9f5",
                    borderColor: "#00d2aa",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#004141",
                    mb: 0.5,
                  }}
                >
                  {COMPETENCY_LABELS[key] || key}
                </Typography>
                <Typography
                  sx={{ fontSize: "13px", color: "#00d2aa", fontWeight: 500 }}
                >
                  {questions?.length || 0} Questions
                </Typography>
              </Paper>
            ))}
          </Box>

          {/* Right Content - Questions */}
          <Box sx={{ flex: 1 }}>
            {questions_by_competency[selectedCompetency] && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#004141",
                    mb: 2,
                  }}
                >
                  {COMPETENCY_LABELS[selectedCompetency] || selectedCompetency}{" "}
                  ({questions_by_competency[selectedCompetency]?.length || 0})
                </Typography>

                {questions_by_competency[selectedCompetency]?.map(
                  (q, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        mb: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              backgroundColor: "#e6f9f5",
                              color: "#00a884",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "13px",
                              fontWeight: 600,
                              flexShrink: 0,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#004141",
                              flex: 1,
                            }}
                          >
                            {q.question}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewCascade(q.cascade_questions)}
                          sx={{
                            fontSize: "12px",
                            textTransform: "none",
                            borderColor: "#e0e0e0",
                            color: "#00d2aa",
                            padding: "4px 12px",
                            borderRadius: "6px",
                            ml: 2,
                            flexShrink: 0,
                            "&:hover": {
                              borderColor: "#00d2aa",
                              backgroundColor: "#f8fffe",
                            },
                          }}
                        >
                          View Cascade
                        </Button>
                      </Box>

                      {/* Show rationale if available */}
                      {q.rationale && (
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#666",
                            fontStyle: "italic",
                            mt: 1,
                            pl: 5,
                          }}
                        >
                          {q.rationale}
                        </Typography>
                      )}
                    </Paper>
                  ),
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666", mb: 3 }}>
            Topics already covered during pre-screening — excluded from this
            interview pack so they are not repeated.
          </Typography>
          {do_not_repeat_list.map((item, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                {item}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666", mb: 3 }}>
            JD must-have requirements not validated during pre-screening —
            targeted validation questions were added to the pack.
          </Typography>
          {must_have_validations.map((item, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                <strong>{item.split("—")[0]}</strong>
                {item.split("—")[1] ? ` — ${item.split("—")[1]}` : ""}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {activeTab === 3 && (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
          {/* Pack Summary */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              backgroundColor: "#fff",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#004141",
                mb: 3,
              }}
            >
              Pack Summary
            </Typography>

            <Typography
              sx={{
                fontSize: "32px",
                fontWeight: 600,
                color: "#00d2aa",
                mb: 1,
              }}
            >
              {pack_summary.total_questions}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#888", mb: 3 }}>
              Total Questions
            </Typography>

            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#888",
                mb: 1.5,
                textTransform: "uppercase",
              }}
            >
              By Competency
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: COMPETENCY_COLORS.technical,
                    }}
                  />
                  <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                    Technical
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 600, color: "#004141" }}
                >
                  {pack_summary.technical_count}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: COMPETENCY_COLORS.behavioral,
                    }}
                  />
                  <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                    Behavioural Competency
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 600, color: "#004141" }}
                >
                  {pack_summary.behavioral_count}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: COMPETENCY_COLORS.communication,
                    }}
                  />
                  <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                    Communication Skills
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 600, color: "#004141" }}
                >
                  {pack_summary.communication_count}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: COMPETENCY_COLORS.future_ready,
                    }}
                  />
                  <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                    Future-Ready Skills
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 600, color: "#004141" }}
                >
                  {pack_summary.future_ready_count}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Generation Details */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              backgroundColor: "#fff",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#004141",
                mb: 3,
              }}
            >
              Generation details
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#004141",
                    mb: 0.5,
                  }}
                >
                  Pre-screening analyzed
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#888" }}>
                  {pack_summary.pre_screening_topics_excluded} topics excluded
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#004141",
                    mb: 0.5,
                  }}
                >
                  Must-have validation questions
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#888" }}>
                  {pack_summary.must_have_validations_added} added
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#004141",
                    mb: 0.5,
                  }}
                >
                  Cascade logic
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#888" }}>
                  Added for all questions
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#004141",
                    mb: 0.5,
                  }}
                >
                  Compliance
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#888" }}>
                  Non-discrimination confirmed
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Cascade Logic Dialog */}
      <Dialog
        open={cascadeDialogOpen}
        onClose={handleCloseCascade}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#004141",
              mb: 3,
            }}
          >
            Cascade Logic
          </Typography>

          {selectedCascade && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Strong Answer Path */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#00a884",
                    mb: 1,
                  }}
                >
                  Strong Answer Path
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#00a884",
                      mt: 0.8,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "14px", color: "#403f3f", lineHeight: 1.6 }}
                  >
                    {selectedCascade.strong_answer}
                  </Typography>
                </Box>
              </Box>

              {/* Vague Answer Path */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#ff9800",
                    mb: 1,
                  }}
                >
                  Vague Answer Path
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#ff9800",
                      mt: 0.8,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "14px", color: "#403f3f", lineHeight: 1.6 }}
                  >
                    {selectedCascade.vague_answer}
                  </Typography>
                </Box>
              </Box>

              {/* Missing Evidence Path */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#e53935",
                    mb: 1,
                  }}
                >
                  Missing Evidence Path
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#e53935",
                      mt: 0.8,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "14px", color: "#403f3f", lineHeight: 1.6 }}
                  >
                    {selectedCascade.missing_evidence}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseCascade}
            sx={{
              fontSize: "14px",
              textTransform: "none",
              color: "#666",
              padding: "8px 20px",
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "#f8f9fa",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
